/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
export type TimeUnit = `d` | `h` | `m` | `ms` | `s`

export type TimeAmount = `${number}${TimeUnit}`

const TIME_FACTORS: Record<TimeUnit, number> = {
  d: 86400000,
  h: 3600000,
  m: 60000,
  ms: 1,
  s: 1000,
}

export const parseTimeAmount = (time: TimeAmount): number => {
  const unit = time.slice(-2) === `ms` ? `ms` : (time.slice(-1) as TimeUnit)
  const amount = unit === `ms` ? time.slice(0, -2) : time.slice(0, -1)
  const factor = TIME_FACTORS[unit]

  return Number(amount) * factor
}

export const MONTHS = [
  `jan`,
  `feb`,
  `mar`,
  `apr`,
  `may`,
  `jun`,
  `jul`,
  `aug`,
  `sep`,
  `oct`,
  `nov`,
  `dec`,
] as const
export type Month = (typeof MONTHS)[number]

export const MONTH_TO_INT_ONE_INDEXED: Record<Month, number> = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
}

export const DAYS_PER_MONTH_LEAP_YEAR: Record<`feb`, number> = {
  feb: 29,
} as const

export const DAYS_PER_MONTH: Record<Month, number> = {
  jan: 31,
  feb: 28,
  mar: 31,
  apr: 30,
  may: 31,
  jun: 30,
  jul: 31,
  aug: 31,
  sep: 30,
  oct: 31,
  nov: 30,
  dec: 31,
} as const

export const HOURS_PER_MONTH: Record<Month, number> = {
  jan: 24 * 31,
  feb: 24 * 28,
  mar: 24 * 31,
  apr: 24 * 30,
  may: 24 * 31,
  jun: 24 * 30,
  jul: 24 * 31,
  aug: 24 * 31,
  sep: 24 * 30,
  oct: 24 * 31,
  nov: 24 * 30,
  dec: 24 * 31,
} as const

export function hoursInMonth(month: number): number {
  return HOURS_PER_MONTH[MONTHS[month]]
}

export const HOURS_PER_YEAR = 365 * 24

export const CUMULATIVE_HOURS_PER_MONTH: number[] = [
  0,
  24 * 31,
  24 * (31 + 28),
  24 * (31 + 28 + 31),
  24 * (31 + 28 + 31 + 30),
  24 * (31 + 28 + 31 + 30 + 31),
  24 * (31 + 28 + 31 + 30 + 31 + 30),
  24 * (31 + 28 + 31 + 30 + 31 + 30 + 31),
  24 * (31 + 28 + 31 + 30 + 31 + 30 + 31 + 31),
  24 * (31 + 28 + 31 + 30 + 31 + 30 + 31 + 31 + 30),
  24 * (31 + 28 + 31 + 30 + 31 + 30 + 31 + 31 + 30 + 31),
  24 * (31 + 28 + 31 + 30 + 31 + 30 + 31 + 31 + 30 + 31 + 30),
  24 * (31 + 28 + 31 + 30 + 31 + 30 + 31 + 31 + 30 + 31 + 30 + 31),
]

/**
 * returns the hour of the year as an integer from the
 * given date
 * @param {Date} date  given date
 * @returns {number}
 */
export function getHourOfYear(date: Date): number {
  const start = new Date(date.getFullYear(), 0, 1)
  const diff = date.getTime() - start.getTime()
  const oneHour = 1000 * 60 * 60
  const result = Math.floor(diff / oneHour)

  return result
}

const isLeapYear = (year: number): boolean =>
  (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0

/**
 * The hour of a 365-day year, as an 8760-hour shape is indexed: in a leap year,
 * February 29 reads February 28's hours and every later hour reads its own
 * calendar day's, not the next day's.
 */
export function getNormalizedHourOfYear(date: Date): number {
  const hour = getHourOfYear(date)
  const afterFebruary28 =
    date.getMonth() > 1 || (date.getMonth() === 1 && date.getDate() === 29)

  return isLeapYear(date.getFullYear()) && afterFebruary28 ? hour - 24 : hour
}
